import decline from "./declension/index.mjs"
import { removeAllAccents } from "./miscUtils.mjs"

/**
 * Capitalizes the first letter of a given string.
 *
 * If the input string is empty or falsy, it returns the string unchanged.
 * Otherwise, it returns a new string with the first character converted to uppercase.
 *
 * @param {string} str - The input string.
 * @returns {string} The string with its first letter capitalized.
 *
 * @example
 * capitalizeFirstLetter("hello") // "Hello"
 * capitalizeFirstLetter("") // ""
 */
function capitalizeFirstLetter(str) {
  if (!str) return str
  return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 * Converts the first letter of a given string to lowercase.
 *
 * If the input string is empty or falsy, it returns the string unchanged.
 * Otherwise, it returns a new string with the first character converted to lowercase.
 *
 * @param {string} str - The input string.
 * @returns {string} The string with its first letter in lowercase.
 *
 * @example
 * lowerCaseFirstLetter("Hello") // "hello"
 * lowerCaseFirstLetter("") // ""
 */
function lowerCaseFirstLetter(str) {
  if (!str) return str
  return str.charAt(0).toLowerCase() + str.slice(1)
}

/**
 * Truncates a string to a specified maximum length and appends an ellipsis (or custom suffix).
 *
 * If the string is shorter than or equal to `maxLength + 3`, it is returned unchanged.
 * Otherwise, it is truncated to `maxLength` characters and the `ellipsis` is appended.
 *
 * @param {string} str - The input string to truncate.
 * @param {number} [maxLength=25] - The maximum length of the truncated string, excluding the ellipsis.
 * @param {string} [ellipsis="..."] - The string to append after truncation.
 * @returns {string|undefined} The truncated string with ellipsis, or `undefined` if input is falsy.
 *
 * @example
 * truncate("This is a very long sentence that needs truncation.") // "This is a very long sentenc..."
 * truncate("Short text") // "Short text"
 * truncate("Another long text", 10, "--") // "Another lo--"
 */
function truncate(str, maxLength = 25, ellipsis = "...") {
  if (!str) return
  if (str.length <= maxLength + 3) return str
  return str.substring(0, maxLength) + ellipsis
}

/**
 * Replaces specific non-breaking space characters in a string with regular spaces.
 *
 * This function replaces Unicode characters `\u202F` (NARROW NO-BREAK SPACE, code 8239)
 * and `\u00A0` (NO-BREAK SPACE, code 160) with standard space characters.
 *
 * @param {string} str - The input string possibly containing non-breaking spaces.
 * @returns {string} The modified string with specific non-breaking spaces replaced by regular spaces.
 *
 * @example
 * replaceASCII("Hello\u00A0World") // "Hello World"
 * replaceASCII("Value:\u202F100") // "Value: 100"
 */
function replaceASCII(str) {
  const arr = []
  if (str) {
    for (let i = 0; i < str.length; i++) {
      if (str.charCodeAt(i) === 8239 || str.charCodeAt(i) === 160) arr.push(" ")
      else arr.push(str.charAt(i))
    }
  }
  return arr.join("")
}

/**
 * Pads the end of a string with spaces until it reaches the specified length.
 *
 * If the input string is shorter than `size`, spaces are appended until the length matches `size`.
 * If it is already `size` characters or longer, the original string is returned unchanged.
 *
 * @param {string} [string=""] - The input string to pad.
 * @param {number} size - The desired final length of the string.
 * @returns {string} The padded string.
 *
 * @example
 * padStringEnd("test", 8) // "test    "
 * padStringEnd("example", 4) // "example"
 */
function padStringEnd(string = "", size) {
  while (string.length < size) string += " "
  return string
}

/**
 * Removes all common whitespace characters from a string, including spaces, tabs,
 * newlines, carriage returns, and specific Unicode spaces, then trims the result.
 *
 * If the input is not a string or is falsy, it is returned unchanged.
 *
 * @param {string} str - The input string to trim.
 * @returns {string} The cleaned and trimmed string.
 *
 * @example
 * trim("  A\tB\nC\r ") // "ABC"
 * trim("  Hello ") // "Hello" (removes non-breaking spaces)
 */
function trim(str) {
  if (!str || typeof str !== "string") return str
  return str
    .replace(/ /g, "") // Spaces
    .replace(/\t/g, "") // Tabs
    .replace(/\r/g, "") // Carriage returns
    .replace(/\n/g, "") // New lines
    .replace(/\u00A0/g, "") // Non-breaking space
    .replace(/\u2003/g, "") // Em spaces
    .trim()
}

/**
 * Applies grammatical declension to a word based on the given locale.
 *
 * This function uses the base language from the locale (e.g., "en" from "en-US")
 * and applies a declension form using an external `decline` function.
 * If any required argument is missing, the original word is returned.
 *
 * @param {string} word - The word to be declined.
 * @param {string} declension - The declension form to apply (e.g., "plural", "genitive").
 * @param {string} locale - The locale string (e.g., "en-US", "fr-FR").
 * @returns {string} The declined form of the word, or the original word if invalid input.
 *
 * @example
 * declineWord("child", "plural", "en-US") // depends on `decline` implementation
 * declineWord("chat", "plural", "fr-FR") // depends on `decline` implementation
 */
function declineWord(word, declension, locale) {
  if (!word || !declension || !locale) return word
  return decline(word, declension, locale.split("-")[0])
}

/**
 * Converts a camelCase or PascalCase string into a spaced label with capitalization.
 *
 * The first character is capitalized. Each subsequent uppercase letter is converted to lowercase and preceded by a space.
 * If the input is falsy, an empty string is returned.
 *
 * @param {string} name - The camelCase or PascalCase string.
 * @returns {string} The formatted label with spaces and capitalization.
 *
 * @example
 * labelFromName("camelCaseExample") // "Camel case example"
 * labelFromName("PascalCase") // "Pascal case"
 * labelFromName("") // ""
 */
function labelFromName(name) {
  if (!name) return ""
  let label = ""
  for (let i = 0; i < name.length; i++) {
    if (i === 0) {
      label += name[0].toUpperCase()
    } else if (name[i] >= "A" && name[i] <= "Z") {
      label += " " + name[i].toLowerCase()
    } else {
      label += name[i]
    }
  }
  return label
}

/**
 * Replaces the last occurrence of a target substring in a string with a replacement string.
 *
 * If the target substring is not found, the original string is returned unchanged.
 *
 * @param {string} str - The original string.
 * @param {string} target - The substring to be replaced (last occurrence only).
 * @param {string} replacement - The string to replace the target with.
 * @returns {string} The resulting string after replacement.
 *
 * @example
 * replaceLast("foo_bar_foo", "foo", "baz") // "foo_bar_baz"
 * replaceLast("hello world", "test", "!") // "hello world" (target not found)
 */
function replaceLast(str, target, replacement) {
  const lastIndex = str.lastIndexOf(target)
  if (lastIndex === -1) return str // Return original string if target is not found

  return str.slice(0, lastIndex) + replacement + str.slice(lastIndex + target.length)
}

/**
 * Checks if a given value is a string primitive or a String object.
 *
 * @param {*} value - The value to check.
 * @returns {boolean} `true` if the value is a string or a String object, otherwise `false`.
 *
 * @example
 * isString("hello") // true
 * isString(new String("hello")) // true
 * isString(123) // false
 */
function isString(value) {
  return typeof value === "string" || value instanceof String
}

/**
 * Converts a string to camelCase format.
 *
 * The function replaces or removes various special characters, splits the string into words,
 * then converts the first word to lowercase and capitalizes the first letter of subsequent words.
 * Symbols like `%`, `+`, `>`, `<`, and `&` are replaced with word equivalents.
 *
 * @param {string} str - The input string to convert.
 * @returns {string} The camelCase version of the input string, or the original value if falsy.
 *
 * @example
 * toCamelCase("hello world") // "helloWorld"
 * toCamelCase("percent% value") // "percentPercentValue"
 * toCamelCase("a/b+c") // "aBandc"
 */
function toCamelCase(str) {
  if (!str) return str
  str = str
    .replace(/\//g, " ")
    .replace(/\./g, "")
    .replace(/_/g, " ")
    .replace(/:/g, "")
    .replace(/%/g, "Percent")
    .replace(/\+/g, "And")
    .replace(/>/g, "After")
    .replace(/</g, "Before")
    .replace(/&/g, "And")
  str = str.replace(/-/g, " ")
  str = str.replace(/'/g, "")
  str = str.replace(/\(/g, "")
  str = str.replace(/\)/g, "")
  let words = str.split(" ")
  if (words.length === 0) return ""
  let name = words[0].toLowerCase()
  for (let i = 1; i < words.length; i++) if (words[i].length > 0) name += words[i][0].toUpperCase() + words[i].substring(1).toLowerCase()
  return name
}

/**
 * Converts a label string to a camelCase name after removing all accents.
 *
 * If the input is falsy or not a string, returns an empty string.
 * Uses `removeAllAccents` to normalize the label before converting it to camelCase with `toCamelCase`.
 *
 * @param {string} label - The label string to convert.
 * @returns {string} The camelCase name derived from the label.
 *
 * @example
 * labelToName("Déjà Vu") // "dejaVu"
 * labelToName("Hello World") // "helloWorld"
 * labelToName("") // ""
 */
function labelToName(label) {
  if (!label || !isString(label)) return ""
  return toCamelCase(removeAllAccents(label))
}

export {
  capitalizeFirstLetter,
  declineWord,
  isString,
  labelFromName,
  labelToName,
  lowerCaseFirstLetter,
  padStringEnd,
  replaceASCII,
  replaceLast,
  toCamelCase,
  trim,
  truncate,
}
